(ns client-portal.client.user-management.views
  (:require [client-portal.client.layout :as l]
            [client-portal.client.user-management.components :as um.c]
            [client-portal.client.user-management.data :as um.d]
            [client-portal.client.user-management.organizations.views :as organizations]
            [client-portal.client.user-management.roles.views :as roles]
            [client-portal.client.user-management.user.data]
            [client-portal.client.user-management.user.views :as user]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn section-panel [_]
  ;; In the narrow scrolling row the selected section can start out of view
  (let [scroll-to-selected (fn [this]
                             (some-> (re/dom-node this)
                                     (.querySelector "[aria-current=page]")
                                     (.scrollIntoView #js {:block "nearest"
                                                           :inline "nearest"})))]
    (re/create-class
      {:component-did-mount scroll-to-selected
       :component-did-update scroll-to-selected
       :reagent-render
       (fn [{:keys [section]}]
         (let [counts {"users" (some-> @(re/subscribe [:user-management/users-]) (um.d/rows) (count))
                       "organizations" (some-> @(re/subscribe [:user-management/organizations-]) (um.d/rows) (count))
                       "roles" (when-let [applications-by-id (not-empty @(re/subscribe [:user-management/applications-by-id-]))]
                                 (some->> @(re/subscribe [:user-management/roles-])
                                          (um.d/rows)
                                          (filter #(contains? applications-by-id (:application-id %)))
                                          (count)))
                       "permissions" (some-> @(re/subscribe [:user-management/permissions-]) (um.d/rows) (count))}]
           [:nav {:aria-label "User management"
                  :class-name "um-section-panel"
                  :style {:border-color (style/alc-color "Border/divider_secondary")}}
            (into [:div {:class-name "um-section-list"}]
                  (for [{:keys [id label icon-type]} um.d/sections
                        :let [active? (= id section)]]
                    ^{:key id}
                    [:button {:type "button"
                              :aria-current (when active? "page")
                              :class-name (str "flex items-center gap-2 rounded-md text-left outline-none transition-colors "
                                               "focus-visible:ring-[3px] focus-visible:ring-ring/50 hover:bg-up-background-hover"
                                               (when active? " bg-up-background-hover"))
                              :style {:border (str "1px solid " (if active? (style/alc-color "Border/divider") "transparent"))
                                      :color (style/alc-color (if active? "Text/primary_header" "Text/primary"))
                                      :cursor :pointer
                                      :font-size 14
                                      :font-weight (if active? 500 400)
                                      :line-height 1.43
                                      :padding "7px 12px"}
                              :on-click #(re/dispatch [:user-management/go :user-management {:section id}])}
                     [alc/up-icon {:icon-type icon-type
                                   :aria-hidden true
                                   :class-name "h-4 w-4"
                                   :style {:color (style/alc-color (if active? "Icons/primary" "Icons/secondary"))}}]
                     [:span {:style {:flex 1}}
                      label]
                     (when-let [n (get counts id)]
                       [:span {:style {:color (style/alc-color "Text/secondary")
                                       :font-size 12
                                       :font-variant-numeric "tabular-nums"
                                       :margin-left 8}}
                        n])]))]))})))

(defn organization-switcher [_]
  (let [open?* (re/ratom false)
        search* (re/ratom "")]
    (fn [{:keys [selected-organization-id]}]
      (let [organizations (um.d/rows @(re/subscribe [:user-management/organizations-]))
            member-counts @(re/subscribe [:user-management/member-counts-])
            user-count (some-> @(re/subscribe [:user-management/users-]) (um.d/rows) (count))
            selected (some #(when (= selected-organization-id (:tenant-organization-id %)) %) organizations)
            search (some-> @search* (s/trim) (s/lower-case) (not-empty))
            matches (filterv #(or (nil? search) (s/includes? (s/lower-case (:label %)) search)) organizations)
            choose (fn [tenant-organization-id]
                     (reset! open?* false)
                     (re/dispatch [:user-management/set-organization tenant-organization-id]))
            all-mark [:span {:aria-hidden true
                             :style {:align-items :center
                                     :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                     :border-radius "50%"
                                     :display :inline-flex
                                     :flex "0 0 24px"
                                     :height 24
                                     :justify-content :center
                                     :width 24}}
                      [alc/up-icon {:icon-type :TreeIcon
                                    :class-name "h-3.5 w-3.5"
                                    :style {:color (style/alc-color "Icons/secondary")}}]]
            option (fn [{:keys [value mark label n]}]
                     (let [selected? (= value selected-organization-id)]
                       [:button {:key (or value "all")
                                 :type "button"
                                 :aria-current (when selected? "true")
                                 :class-name um.c/option-class-name
                                 :style {:color (style/alc-color "Text/primary")
                                         :cursor :pointer
                                         :font-size 14
                                         :padding "6px 8px"}
                                 :on-click #(choose value)}
                        mark
                        [:span {:style {:flex 1
                                        :min-width 0
                                        :overflow :hidden
                                        :text-overflow :ellipsis
                                        :white-space :nowrap}}
                         label]
                        (when n
                          [um.c/secondary-text {:style {:font-size 12
                                                        :font-variant-numeric "tabular-nums"}}
                           n])
                        [alc/up-icon {:icon-type :DoneIcon
                                      :class-name (str "h-4 w-4 shrink-0 " (if selected? "opacity-100" "opacity-0"))}]]))]
        [:span {:style {:align-items :center
                        :display :inline-flex
                        :gap 2
                        :max-width "100%"
                        :min-width 0}}
         [alc/up-popover
          {:open @open?*
           :on-open-change #(do (reset! open?* %)
                                (reset! search* ""))
           :align :start
           :class-name "w-max p-0"
           :trigger [:button {:id "um-organization-switcher"
                              :type "button"
                              :class-name um.c/popover-trigger-class-name
                              :disabled (nil? organizations)
                              :style {:cursor :pointer
                                      :max-width "100%"}}
                     (if selected
                       [um.c/organization-mark selected {:size 20}]
                       [alc/up-secondary-icon {:icon-type :TreeIcon}])
                     [:span {:class-name "truncate"}
                      (if selected (:label selected) "All Organizations")]
                     [alc/up-icon {:icon-type :ChevronDownIcon
                                   :class-name (str "h-4 w-4 shrink-0 transition-transform duration-200"
                                                    (when @open?* " -rotate-180"))}]]
           :content [:div {:style {:display :flex
                                   :flex-direction :column
                                   :max-width "calc(100vw - 32px)"
                                   :width 320}}
                     (when (< 6 (count organizations))
                       [:div {:style {:border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                      :padding 8}}
                        [alc/up-input {:auto-focus true
                                       :default-value ""
                                       :aria-label "Search organizations"
                                       :placeholder "Search organizations"
                                       :on-change #(reset! search* (.. % -target -value))}]])
                     [:div {:style {:max-height 360
                                    :overflow-y :auto
                                    :padding 4}}
                      (when-not search
                        [:<>
                         (option {:mark all-mark
                                  :label "All Organizations"
                                  :n user-count})
                         [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                        :margin "4px 0"}}]])
                      (if (empty? matches)
                        [:div {:style {:padding 12}}
                         [um.c/secondary-text {} "No organizations match. Try part of a name instead."]]
                        (into [:<>]
                              (for [{:keys [tenant-organization-id label] :as organization} matches]
                                (option {:value tenant-organization-id
                                         :mark [um.c/organization-mark organization {:size 24}]
                                         :label label
                                         :n (get member-counts tenant-organization-id 0)}))))]]}]
         (when selected
           [alc/up-ghost-button {:icon-type :CrossIcon
                                 :icon "sm"
                                 :aria-label "Show all organizations"
                                 :tooltip-content "Show All Organizations"
                                 :on-click (fn []
                                             (choose nil)
                                             (some-> (js/document.getElementById "um-organization-switcher") (.focus)))}])]))))

(defn users-toolbar []
  (let [{selected-organization-id :tenant-organization-id :keys [status]} @(re/subscribe [:user-management/filters-])
        invitations-by-email @(re/subscribe [:user-management/invitations-by-email-])]
    [um.c/toolbar
     [um.c/tenant-dropdown]
     (when-not @(re/subscribe [:user-management/manager?-])
       [organization-switcher {:selected-organization-id selected-organization-id}])
     [:div {:style {:flex "1 1 220px"
                    :max-width 320}}
      [alc/up-input {:form-path um.d/filters-path
                     :form-name :search
                     :type "search"
                     :aria-label "Search by email"
                     :placeholder "Search by email"}]]
     [alc/up-popover-button-dropdown {:placeholder "Any Status"
                                      :fixed-icon-type :FilterIcon
                                      :align :start
                                      :clearable? true
                                      :disabled (nil? invitations-by-email)
                                      :options (mapv (fn [s]
                                                       {:text (um.d/status-labels s)
                                                        :value s
                                                        :selected? (= s status)})
                                                     [:active :invited :expired :inactive])
                                      :on-change #(re/dispatch [:user-management/set-filter :status %])}]
     [um.c/spacer]
     (when @(re/subscribe [:account/has-permission?- :create:users])
       [alc/up-call-to-action-button {:icon-type :AddIcon
                                      :on-click #(re/dispatch [:user-management/go :user-management/user {:user-id "new"}])}
        "Add User"])]))

(defn users-table []
  (let [users @(re/subscribe [:user-management/users-])
        filtered @(re/subscribe [:user-management/filtered-users-])
        filters @(re/subscribe [:user-management/filters-])
        organizations-by-id @(re/subscribe [:user-management/organizations-by-id-])
        roles-by-id @(re/subscribe [:user-management/roles-by-id-])
        applications-by-id @(re/subscribe [:user-management/applications-by-id-])
        invitations-by-email @(re/subscribe [:user-management/invitations-by-email-])
        member-counts @(re/subscribe [:user-management/member-counts-])
        compact? (not @(re/subscribe [:core/breakpoint-min?- :lg]))
        client-portal-id (some (fn [[id {:keys [external-id]}]] (when (= "client-portal" external-id) id)) applications-by-id)
        organization (get organizations-by-id (:tenant-organization-id filters))
        narrowed? (or (not-empty (:search filters)) (:status filters))
        scope-count (if organization
                      (get member-counts (:tenant-organization-id organization) 0)
                      (count (um.d/rows users)))
        in-organization (when organization
                          (str " in " (:label organization)))]
    (cond
      (= users :error)
      [empty-state/no-data-card {:text "People couldn't be loaded. Reload the page to try again."
                                 :material-icon-type :error-outline}]

      (nil? filtered)
      [empty-state/loading-card {:label "Loading people…"}]

      (and (empty? filtered) narrowed?)
      [:div {:style {:align-items :center
                     :display :flex
                     :flex-direction :column
                     :gap 12
                     :padding "48px 16px"}}
       [:span {:style {:color (style/alc-color "Text/primary_header")
                       :font-size 16}}
        (str "No one" in-organization " matches these filters")]
       [alc/up-button {:on-click #(re/dispatch [:user-management/clear-filters])}
        "Clear Filters"]]

      (and (empty? filtered) organization)
      [empty-state/no-data-card {:text (str "No one belongs to " (:label organization) " yet. Add someone to give them access.")
                                 :material-icon-type :group-add}]

      (empty? filtered)
      [empty-state/no-data-card {:text "No one has access here yet. Add someone to get started."
                                 :material-icon-type :group-add}]

      :else
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 8
                     :max-width um.c/max-content-width}}
       [um.c/secondary-text {}
        (if narrowed?
          (str "Showing " (count filtered) " of " (um.c/count-text scope-count "person" "people") in-organization)
          (str (um.c/count-text (count filtered) "person" "people") in-organization))]
       [alc/table {:no-data-text "No people"
                   :row-style (cond-> {:align-items :center}
                                      compact? (assoc :padding "14px 0"))
                   :flex-vals (if compact?
                                [1]
                                [2 "0 0 128px" 1.4 1.6 "0 0 120px"])
                   :headers (if compact?
                              ["User"]
                              ["Email" "Status" "Organizations" "Roles" "Location Access"])
                   :rows (for [{:keys [public-id email-address tenant-organization-ids application-ids application-role-ids
                                       cohort-ids location-ids]
                                :as u} filtered
                               :let [status (um.d/user-status u invitations-by-email)
                                     client-portal-roles (->> application-role-ids
                                                              (keep roles-by-id)
                                                              (filter #(= client-portal-id (:application-id %)))
                                                              (map :label)
                                                              (sort))
                                     other-applications (->> application-ids
                                                             (remove #{client-portal-id})
                                                             (keep #(get-in applications-by-id [% :label])))
                                     organizations (->> tenant-organization-ids
                                                        (keep #(get-in organizations-by-id [% :label]))
                                                        (um.c/join-words))
                                     roles [:span {:style {:display :flex
                                                           :flex-direction :column
                                                           :gap 4}}
                                            (if (not-empty client-portal-roles)
                                              [um.c/role-pills client-portal-roles]
                                              [um.c/secondary-text {:style {:font-size 12}} "No roles"])
                                            (when (not-empty other-applications)
                                              [um.c/secondary-text {:style {:font-size 12}}
                                               (str "Also " (um.c/join-words other-applications))])]
                                     location-access (if (or (not-empty cohort-ids) (not-empty location-ids))
                                                       "Limited"
                                                       [um.c/secondary-text {:style {:font-size 12}} "Every location"])]]
                           {:cells (if compact?
                                     [{:label [:div {:style {:display :flex
                                                             :flex-direction :column
                                                             :gap 10
                                                             :min-width 0}}
                                               [:span {:style {:font-weight 500
                                                               :overflow-wrap :anywhere}}
                                                email-address]
                                               (into [:dl {:style {:align-items :baseline
                                                                   :column-gap 16
                                                                   :display :grid
                                                                   :grid-template-columns "auto minmax(0, 1fr)"
                                                                   :margin 0
                                                                   :row-gap 8}}]
                                                     (mapcat (fn [[term detail]]
                                                               [[:dt {:style {:color (style/alc-color "Text/secondary")}}
                                                                 term]
                                                                [:dd {:style {:margin 0
                                                                              :min-width 0}}
                                                                 detail]])
                                                             [["Status" [um.c/status-badge status]]
                                                              ["Organizations" organizations]
                                                              ["Roles" roles]
                                                              ["Locations" location-access]]))]}]
                                     [{:label email-address
                                       :style {:overflow-wrap :anywhere}}
                                      {:label [um.c/status-badge status]}
                                      {:label organizations}
                                      {:label roles}
                                      {:label location-access}])
                            :on-click #(re/dispatch [:user-management/go :user-management/user {:user-id public-id}])})}]])))

(defn users-section []
  [l/standard-content-container {:title "Users"
                                 :header-contents [users-toolbar]
                                 :sticky-header? true
                                 :container-style {:flex 1
                                                   :min-height :auto}}
   [users-table]])

(defn root [_]
  (let [manages-users?- (re/subscribe [:account/has-some-role?- [:user-manager]])
        manager?- (re/subscribe [:user-management/manager?-])]
    (fn [{:keys [section user-id] :as args}]
      [l/logged-in-with-header-nav-core (merge args {:header-filters? false
                                                     :concept-dropdown? false
                                                     :location-dropdown? false
                                                     :crew-type-radio? false
                                                     :timeframe-dropdown? false})
       (if-not @manages-users?-
         [empty-state/no-data-card {:text "Only admins and user managers can manage users."
                                    :material-icon-type :lock}]
         [:div {:class-name "um-layout"}
          (when-not @manager?-
            [section-panel {:section section}])
          [:div {:style {:display :flex
                         :flex 1
                         :flex-direction :column
                         :min-width 0}}
           (cond
             (= "users" section) (if user-id
                                   ^{:key user-id} [user/root {:user-id user-id}]
                                   [users-section])
             @manager?- [empty-state/no-data-card {:text "Only admins can change organizations, roles and permissions."
                                                   :material-icon-type :lock}]
             :else (case section
                     "organizations" [organizations/section]
                     "roles" [roles/roles-section]
                     "permissions" [roles/permissions-section]
                     [empty-state/no-data-card {:text "There's nothing here. Choose a section from the list."}]))]])])))
